import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Loader, Button, Input, Tooltip, Typography, DatePicker, Checkbox, Switch, Modal, ModalContent, ModalHeading, ModalActions, ModalButton, ModalDescription, ProgressBar, Tabs, Tab } from "@launchmetrics/lm-ui";
import { SearchOutline, DeviceFloppyOutline, DownloadOutline } from '@launchmetrics/lm-icons';
import PropTypes from "prop-types";
import { useHistory, useLocation } from "react-router-dom";
import GenericDropdownMenu from "components/LM/common/GenericDropdownMenu";
import K2Api from 'lib/ajax/K2Api';
import { useK2Loader } from 'hooks/useK2Loader';
import { buildVoicesCsv, downloadCsv } from 'components/LM/Voice/ValidationSearch/voiceCsvExport';
import './voiceValidationSearch.scss';

const k2 = new K2Api();

const CSV_EXPORT_LIMIT = 5000;
const CSV_PAGE_SIZE = 100;
const SEARCH_TOTAL_CAP = 10000;

function formatVoiceCount(count) {
	return count >= SEARCH_TOTAL_CAP ? `${SEARCH_TOTAL_CAP.toLocaleString()}+` : count.toLocaleString();
}


const VOICE_OPTIONS = ["media", "brand", "influencer", "celebrity"];
const CHANNEL_OPTIONS = [
	"facebook",
	"instagram",
	"tiktok",
	"twitter",
	"youtube",
	"wechat_public",
	"bilibili",
	"douyin",
	"weibo",
	"xiaohongshu",
	"onlineMedia",
	"printMediaDmr"
];

const STATUS_OPTIONS = [
	{ value: "active", label: "Active" },
	{ value: "inactive", label: "Inactive" },
	{ value: "candidate", label: "Candidate" }
];

const VALIDATED_OPTIONS = [
	{ value: "true", label: "True" },
	{ value: "false", label: "False" }
];

function parseShorthand(value) {
	if (!value) return null;
	const str = String(value).trim().toUpperCase();
	const match = str.match(/^(\d+\.?\d*)\s*([KMB]?)$/);
	if (!match) return null;
	const num = parseFloat(match[1]);
	const suffix = match[2];
	if (suffix === 'K') return Math.round(num * 1000);
	if (suffix === 'M') return Math.round(num * 1000000);
	if (suffix === 'B') return Math.round(num * 1000000000);
	return Math.round(num);
}

const EMPTY_VALUES = {
	term: '',
	type: [],
	channel: [],
	country: [],
	labels: [],
	status: [],
	validated: [],
	upload_csv_file_id: [],
	last_edited_by: [],
	country_confidence_enabled: false,
	country_confidence_min: 0,
	country_confidence_max: 100,
	type_confidence_enabled: false,
	type_confidence_min: 0,
	type_confidence_max: 100,
	missing_mandatory_fields: false,
	to_merge_only: false,
	has_recent_publications: false,
	has_recent_brand_mentions: false,
	voice_name_duplicates: false,
	follower_channels: [],
	followers_min: '',
	followers_max: '',
	last_edited_date_start: null,
	last_edited_date_end: null
};

function buildFiltersPayload(values, labelsCatalog) {
	const filters = {};
	if (values.term) filters.voice_name = values.term;
	if (values.type.length) filters.type = values.type;
	if (values.channel.length) filters.channels = values.channel;
	if (values.country.length) filters.country = values.country;
	if (values.labels.length) {
		const labelIds = values.labels.map(name => {
			const item = (labelsCatalog || []).find(l => (l.name ?? String(l.id)) === name);
			return item ? String(item.id) : name;
		});
		filters.labels = labelIds;
	}
	if (values.status.length) filters.status = values.status;
	if (values.validated.length) filters.validated = values.validated[0] === 'true';
	if (values.upload_csv_file_id.length) filters.upload_csv_file = values.upload_csv_file_id;
	if (values.last_edited_by.length) filters.last_edited_by = values.last_edited_by;
	if (values.country_confidence_enabled) {
		filters.country_confidence_min = values.country_confidence_min;
		filters.country_confidence_max = values.country_confidence_max;
	}
	if (values.type_confidence_enabled) {
		filters.type_confidence_min = values.type_confidence_min;
		filters.type_confidence_max = values.type_confidence_max;
	}
	if (values.missing_mandatory_fields) filters.missing_mandatory_fields = true;
	if (values.to_merge_only) filters.to_merge_only = true;
	if (values.has_recent_publications) filters.has_recent_publications = false;
	if (values.has_recent_brand_mentions) filters.has_recent_brand_mentions = false;
	if (values.voice_name_duplicates) filters.voice_name_duplicates = true;
	if (values.follower_channels.length) filters.follower_channels = values.follower_channels;
	if (values.followers_min) {
		const parsed = parseShorthand(values.followers_min);
		if (parsed !== null) filters.followers_min = parsed;
	}
	if (values.followers_max) {
		const parsed = parseShorthand(values.followers_max);
		if (parsed !== null) filters.followers_max = parsed;
	}
	if (values.last_edited_date_start) {
		const d = values.last_edited_date_start;
		filters.last_edit_date_start = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
	}
	if (values.last_edited_date_end) {
		const d = values.last_edited_date_end;
		filters.last_edit_date_end = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
	}
	return filters;
}

function buildSearchBody(values, labelsCatalog) {
	const body = {};
	if (values.term) body.term = values.term;
	if (values.type.length) body.type = values.type;
	if (values.channel.length) body.channel = values.channel;
	if (values.country.length) body.country = values.country;
	if (values.labels.length) {
		const labelIds = values.labels
			.map(name => (labelsCatalog || []).find(l => (l.name ?? String(l.id)) === name))
			.filter(Boolean)
			.map(item => Number(item.id));
		if (labelIds.length) body.labels = labelIds;
	}
	if (values.status.length) body.status = values.status;
	if (values.validated.length) body.validated = values.validated[0] === 'true';
	if (values.upload_csv_file_id.length) body.upload_csv_file_id = values.upload_csv_file_id.map(Number);
	if (values.last_edited_by.length) body.last_edited_by = values.last_edited_by;
	if (values.country_confidence_enabled) {
		body.country_confidence_min = values.country_confidence_min;
		body.country_confidence_max = values.country_confidence_max;
	}
	if (values.type_confidence_enabled) {
		body.type_confidence_min = values.type_confidence_min;
		body.type_confidence_max = values.type_confidence_max;
	}
	if (values.missing_mandatory_fields) body.missing_mandatory_fields = true;
	if (values.to_merge_only) body.to_merge_only = true;
	if (values.has_recent_publications) body.has_recent_publications = false;
	if (values.has_recent_brand_mentions) body.has_recent_brand_mentions = false;
	if (values.voice_name_duplicates) body.voice_name_duplicates = true;
	if (values.follower_channels.length) body.follower_channels = values.follower_channels;
	if (values.followers_min) {
		const parsed = parseShorthand(values.followers_min);
		if (parsed !== null) body.followers_min = parsed;
	}
	if (values.followers_max) {
		const parsed = parseShorthand(values.followers_max);
		if (parsed !== null) body.followers_max = parsed;
	}
	if (values.last_edited_date_start) {
		const d = values.last_edited_date_start;
		body.last_edited_date_start = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T00:00:00+00:00`;
	}
	if (values.last_edited_date_end) {
		const d = values.last_edited_date_end;
		body.last_edited_date_end = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T23:59:59+00:00`;
	}
	return body;
}

const FILTER_PARAM_KEYS = [
	'term', 'type', 'channel', 'country', 'labels', 'status', 'validated',
	'upload_csv_file_id', 'last_edited_by',
	'country_confidence_min', 'country_confidence_max', 'type_confidence_min', 'type_confidence_max',
	'missing_mandatory_fields', 'to_merge_only', 'has_recent_publications', 'has_recent_brand_mentions',
	'voice_name_duplicates', 'follower_channels', 'followers_min', 'followers_max',
	'last_edited_date_start', 'last_edited_date_end'
];

function hasAnyFilterParam(searchParams) {
	return FILTER_PARAM_KEYS.some(key => Boolean(searchParams[key]));
}

function parseVoiceIds(text) {
	return text
		.split(/[\n,]+/)
		.map(s => s.trim())
		.filter(Boolean);
}

function getVoiceIdsError(text) {
	const entries = text
		.split(/[\n,]+/)
		.map(s => s.trim())
		.filter(Boolean);
	const invalid = entries.filter(s => !/^\d+$/.test(s));
	if (invalid.length === 0) return null;
	return `Invalid voice IDs (only numbers allowed): ${invalid.join(', ')}`;
}

function VoiceValidationSearchForm({loading, searchParams}) {
	const history = useHistory();
	const location = useLocation();

	const { control, handleSubmit, reset, formState, watch, setValue } = useForm({
		defaultValues: {
			term: searchParams.term || '',
			type: searchParams.type ? searchParams.type.split(',') : [],
			channel: searchParams.channel ? searchParams.channel.split(',') : [],
			country: searchParams.country ? searchParams.country.split(',') : [],
			labels: searchParams.labels ? searchParams.labels.split(',') : [],
			status: searchParams.status ? searchParams.status.split(',') : [],
			validated: searchParams.validated ? searchParams.validated.split(',') : [],
			upload_csv_file_id: searchParams.upload_csv_file_id ? searchParams.upload_csv_file_id.split(',') : [],
			last_edited_by: searchParams.last_edited_by ? searchParams.last_edited_by.split(',') : [],
			country_confidence_enabled: !!(searchParams.country_confidence_min || searchParams.country_confidence_max),
			country_confidence_min: searchParams.country_confidence_min ? Number(searchParams.country_confidence_min) : 0,
			country_confidence_max: searchParams.country_confidence_max ? Number(searchParams.country_confidence_max) : 100,
			type_confidence_enabled: !!(searchParams.type_confidence_min || searchParams.type_confidence_max),
			type_confidence_min: searchParams.type_confidence_min ? Number(searchParams.type_confidence_min) : 0,
			type_confidence_max: searchParams.type_confidence_max ? Number(searchParams.type_confidence_max) : 100,
			missing_mandatory_fields: searchParams.missing_mandatory_fields === 'true',
			to_merge_only: searchParams.to_merge_only === 'true',
			has_recent_publications: searchParams.has_recent_publications === 'false',
			has_recent_brand_mentions: searchParams.has_recent_brand_mentions === 'false',
			voice_name_duplicates: searchParams.voice_name_duplicates === 'true',
			follower_channels: searchParams.follower_channels ? searchParams.follower_channels.split(',') : [],
			followers_min: searchParams.followers_min || '',
			followers_max: searchParams.followers_max || '',
			last_edited_date_start: searchParams.last_edited_date_start ? new Date(searchParams.last_edited_date_start) : null,
			last_edited_date_end: searchParams.last_edited_date_end ? new Date(searchParams.last_edited_date_end) : null
		},
		mode: 'onChange'
	});

	const [activeTab, setActiveTab] = useState(searchParams.voice_ids ? 'voiceIds' : 'filters');
	const [voiceIdsText, setVoiceIdsText] = useState(searchParams.voice_ids ? searchParams.voice_ids.split(',').join('\n') : '');
	const [combineWithFilters, setCombineWithFilters] = useState(
		searchParams.combine_with_filters === 'true'
		|| (Boolean(searchParams.voice_ids) && hasAnyFilterParam(searchParams))
	);
	const [saveDialogOpen, setSaveDialogOpen] = useState(false);
	const [listName, setListName] = useState('');
	const [saving, setSaving] = useState(false);
	const [csvModalOpen, setCsvModalOpen] = useState(false);
	const [csvCount, setCsvCount] = useState(null);
	const [csvFetched, setCsvFetched] = useState(null);
	const [csvError, setCsvError] = useState(null);
	const csvAbortedRef = useRef(false);

	const listId = searchParams.list_id || null;

	useEffect(() => {
		if (!listId) return;
		let cancel = false;
		(async () => {
			try {
				const res = await k2.get(`/voices/validation_filter_lists/${listId}`);
				if (cancel) return;
				const f = res.data?.filters || {};
				setListName(res.data?.name || '');
				if (Array.isArray(f.voice_ids) && f.voice_ids.length) {
					setActiveTab('voiceIds');
					setVoiceIdsText(f.voice_ids.join('\n'));
					setCombineWithFilters(
						f.combine_with_filters === true
						|| Object.keys(f).some(key => key !== 'voice_ids')
					);
				} else {
					setVoiceIdsText('');
					setCombineWithFilters(false);
				}
				reset({
					...EMPTY_VALUES,
					term: f.voice_name || f.name || '',
					type: Array.isArray(f.type) ? f.type : [],
					channel: Array.isArray(f.channels) ? f.channels : (Array.isArray(f.channel) ? f.channel : []),
					country: Array.isArray(f.country) ? f.country : [],
					labels: Array.isArray(f.labels) ? f.labels : [],
					status: Array.isArray(f.status) ? f.status : [],
					validated: typeof f.validated === 'boolean' ? [String(f.validated)] : (Array.isArray(f.validated) ? f.validated : []),
					upload_csv_file_id: Array.isArray(f.upload_csv_file) ? f.upload_csv_file : [],
					last_edited_by: Array.isArray(f.last_edited_by) ? f.last_edited_by : [],
					country_confidence_enabled: !!(f.country_confidence_min != null || f.country_confidence_max != null),
					country_confidence_min: f.country_confidence_min ?? 0,
					country_confidence_max: f.country_confidence_max ?? 100,
					type_confidence_enabled: !!(f.type_confidence_min != null || f.type_confidence_max != null),
					type_confidence_min: f.type_confidence_min ?? 0,
					type_confidence_max: f.type_confidence_max ?? 100,
					missing_mandatory_fields: !!f.missing_mandatory_fields,
					to_merge_only: !!f.to_merge_only,
					has_recent_publications: f.has_recent_publications != null,
					has_recent_brand_mentions: f.has_recent_brand_mentions != null,
					voice_name_duplicates: !!f.voice_name_duplicates,
					follower_channels: Array.isArray(f.follower_channels) ? f.follower_channels : [],
					followers_min: f.followers_min != null ? String(f.followers_min) : '',
					followers_max: f.followers_max != null ? String(f.followers_max) : '',
					last_edited_date_start: f.last_edit_date_start ? new Date(f.last_edit_date_start) : null,
					last_edited_date_end: f.last_edit_date_end ? new Date(f.last_edit_date_end) : null,
				});
			} catch {
				// ignore fetch error
			}
		})();
		return () => { cancel = true; };
	}, [listId, reset]);

	const [loadingCountries, countries] = useK2Loader('/countries');
	const [loadingLabels, labelsCatalog] = useK2Loader('/labels');
	const [loadingEditedByUsers, editedByUsers] = useK2Loader('/voices/search/filters/last_edited_by_users');

	const [csvSearch, setCsvSearch] = useState('');
	const [csvFiles, setCsvFiles] = useState([]);
	const [loadingCsvFiles, setLoadingCsvFiles] = useState(true);
	const [csvNamesById, setCsvNamesById] = useState({});

	useEffect(() => {
		let cancel = false;
		const delay = csvSearch ? 300 : 0;
		const timer = setTimeout(async () => {
			try {
				const response = await k2.get('/voices/batch-request', {
					params: { from: 0, size: 100, ...(csvSearch ? { name: csvSearch } : {}) }
				});
				if (cancel) return;
				const results = response.data?.results || [];
				setCsvFiles(results);
				setCsvNamesById(prev => ({
					...prev,
					...Object.fromEntries(results.map(batch => [String(batch.id), batch.name]))
				}));
			} catch {
				if (!cancel) setCsvFiles([]);
			} finally {
				if (!cancel) setLoadingCsvFiles(false);
			}
		}, delay);

		return () => { cancel = true; clearTimeout(timer); };
	}, [csvSearch]);

	const watchedChannels = watch('channel');
	useEffect(() => {
		const currentFollowerChannels = watch('follower_channels');
		const validFollowerChannels = currentFollowerChannels.filter(ch => watchedChannels.includes(ch));
		if (validFollowerChannels.length !== currentFollowerChannels.length) {
			setValue('follower_channels', validFollowerChannels);
		}
		if (!watchedChannels.length) {
			setValue('followers_min', '');
			setValue('followers_max', '');
		}
	}, [watchedChannels, setValue, watch]);

	useEffect(() => {
		if (!labelsCatalog || !searchParams.labels) return;
		const ids = searchParams.labels.split(',');
		const names = ids.map(id => {
			const item = labelsCatalog.find(l => String(l.id) === id);
			return item?.name || id;
		});
		reset(prev => ({ ...prev, labels: names }));
	}, [labelsCatalog, searchParams.labels, reset]);


	const appendFilterParams = (params, values) => {
		if (values.term) params.set('term', values.term);
		if (values.type.length) params.set('type', values.type.join(','));
		if (values.channel.length) params.set('channel', values.channel.join(','));
		if (values.country.length) params.set('country', values.country.join(','));
		if (values.labels.length) {
			const labelIds = values.labels.map(name => {
				const item = (labelsCatalog || []).find(l => (l.name ?? String(l.id)) === name);
				return item ? String(item.id) : name;
			});
			params.set('labels', labelIds.join(','));
		}
		if (values.status.length) params.set('status', values.status.join(','));
		if (values.validated.length) params.set('validated', values.validated.join(','));
		if (values.upload_csv_file_id.length) params.set('upload_csv_file_id', values.upload_csv_file_id.join(','));
		if (values.last_edited_by.length) params.set('last_edited_by', values.last_edited_by.join(','));
		if (values.country_confidence_enabled) {
			params.set('country_confidence_min', values.country_confidence_min);
			params.set('country_confidence_max', values.country_confidence_max);
		}
		if (values.type_confidence_enabled) {
			params.set('type_confidence_min', values.type_confidence_min);
			params.set('type_confidence_max', values.type_confidence_max);
		}
		if (values.missing_mandatory_fields) params.set('missing_mandatory_fields', 'true');
		if (values.to_merge_only) params.set('to_merge_only', 'true');
		if (values.has_recent_publications) params.set('has_recent_publications', 'false');
		if (values.has_recent_brand_mentions) params.set('has_recent_brand_mentions', 'false');
		if (values.voice_name_duplicates) params.set('voice_name_duplicates', 'true');
		if (values.follower_channels.length) params.set('follower_channels', values.follower_channels.join(','));
		if (values.followers_min) {
			const parsed = parseShorthand(values.followers_min);
			if (parsed !== null) params.set('followers_min', parsed);
		}
		if (values.followers_max) {
			const parsed = parseShorthand(values.followers_max);
			if (parsed !== null) params.set('followers_max', parsed);
		}
		if (values.last_edited_date_start) {
			const d = values.last_edited_date_start;
			const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
			params.set('last_edited_date_start', dateStr + 'T00:00:00+00:00');
		}
		if (values.last_edited_date_end) {
			const d = values.last_edited_date_end;
			const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
			params.set('last_edited_date_end', dateStr + 'T23:59:59+00:00');
		}
	};

	const onFormSubmit = (values) => {
		const params = new URLSearchParams();
		if (listId) params.set('list_id', listId);

		if (combineWithFilters || activeTab === 'voiceIds') {
			const ids = parseVoiceIds(voiceIdsText);
			if (ids.length) params.set('voice_ids', ids.join(','));
		}
		if (combineWithFilters) params.set('combine_with_filters', 'true');
		if (combineWithFilters || activeTab === 'filters') {
			appendFilterParams(params, values);
		}

		history.replace({ pathname: location.pathname, search: params.toString() });
	};

	const openSaveDialog = () => {
		setSaveDialogOpen(true);
	};

	const handleSaveList = async (forceNew = false) => {
		if (!listName.trim()) return;
		setSaving(true);
		try {
			let filters = {};
			if (combineWithFilters || activeTab === 'filters') {
				filters = buildFiltersPayload(watch(), labelsCatalog);
			}
			if (combineWithFilters || activeTab === 'voiceIds') {
				filters = { ...filters, voice_ids: parseVoiceIds(voiceIdsText) };
			}
			if (combineWithFilters) filters = { ...filters, combine_with_filters: true };
			const payload = { name: listName.trim(), filters };
			if (listId && !forceNew) {
				await k2.put(`/voices/validation_filter_lists/${listId}`, payload);
			} else {
				await k2.post('/voices/validation_filter_lists', payload);
			}
			setSaveDialogOpen(false);
			alert('List saved successfully');
		} catch {
			alert('Failed to save list');
		} finally {
			setSaving(false);
		}
	};

	const onClearFilters = () => {
		reset({ ...EMPTY_VALUES });
		setVoiceIdsText('');
		setCombineWithFilters(false);
		history.replace({ pathname: location.pathname, search: '' });
	};

	const voiceOptions = VOICE_OPTIONS.map(type => {
		return { value: type, label: type.charAt(0).toUpperCase() + type.slice(1) };
	});

	const channelOptions = CHANNEL_OPTIONS.map(type => {
		let label = type.charAt(0).toUpperCase() + type.slice(1);
		if (type === "wechat_public") {
			label = "Wechat";
		} else if (type === "twitter") {
			label = "Twitter/X";
		} else if (type === "onlineMedia") {
			label = "Online";
		} else if (type === "printMediaDmr") {
			label = "Print";
		}
		return { value: type, label };
	});

	const countryOptions = (countries || []).map(option => ({ value: option.id, label: option.name }));
	const csvFileOptions = csvFiles.map(batch => ({ value: String(batch.id), label: batch.name }));
	const editedByOptions = (editedByUsers || []).map(name => ({ value: name, label: name }));
	const labelOptions = (labelsCatalog || []).map(item => ({
        value: item.name ?? String(item.id),
        label: item.name ?? String(item.id)
    }));

	const isBusy = loading;
	const hasParams = location.search.length > 0;
	const voiceIdsParsed = parseVoiceIds(voiceIdsText);
	const voiceIdsError = voiceIdsText.trim() ? getVoiceIdsError(voiceIdsText) : null;
	const idsRequired = combineWithFilters || activeTab === 'voiceIds';
	const canSubmit = !isBusy && (
		idsRequired
			? voiceIdsParsed.length > 0 && !voiceIdsError
			: formState.isDirty || hasParams
	);

	const voiceIdsValid = voiceIdsParsed.length > 0 && !voiceIdsError;
	const csvSearchBody = {
		...(combineWithFilters || activeTab === 'filters' ? buildSearchBody(watch(), labelsCatalog) : {}),
		...(idsRequired && voiceIdsValid ? { voice_ids: voiceIdsParsed } : {})
	};
	const hasActiveFilters = Object.keys(csvSearchBody).length > 0;
	const canDownloadCsv = hasActiveFilters && !isBusy && (!idsRequired || voiceIdsValid);
	const csvDownloading = csvFetched !== null;

	const handleDownloadCsv = async () => {
		if (!canDownloadCsv) return;
		csvAbortedRef.current = false;
		setCsvModalOpen(true);
		setCsvCount(null);
		setCsvFetched(null);
		setCsvError(null);
		try {
			const res = await k2.post('/voices/search', { ...csvSearchBody, from: 0, size: 1 });
			if (csvAbortedRef.current) return;
			setCsvCount(res.data?.total ?? 0);
		} catch (e) {
			if (csvAbortedRef.current) return;
			setCsvError(e?.response?.data?.message || e?.message || 'Failed to count matching voices');
		}
	};

	const runCsvExport = async () => {
		const total = Math.min(csvCount, CSV_EXPORT_LIMIT);
		csvAbortedRef.current = false;
		setCsvError(null);
		setCsvFetched(0);
		try {
			const voices = [];
			for (let from = 0; from < total; from += CSV_PAGE_SIZE) {
				const size = Math.min(CSV_PAGE_SIZE, total - from);
				const res = await k2.post('/voices/search', { ...csvSearchBody, from, size });
				if (csvAbortedRef.current) return;
				const page = res.data?.voices || [];
				voices.push(...page);
				setCsvFetched(from + size);
				if (!page.length) break;
			}
			const stamp = new Date().toISOString().slice(0, 10);
			downloadCsv(`voices-validation-${stamp}.csv`, buildVoicesCsv(voices));
			closeCsvModal();
		} catch (e) {
			if (csvAbortedRef.current) return;
			setCsvError(e?.response?.data?.message || e?.message || 'Failed to export voices');
			setCsvFetched(null);
		}
	};

	const closeCsvModal = () => {
		csvAbortedRef.current = true;
		setCsvModalOpen(false);
		setCsvCount(null);
		setCsvFetched(null);
		setCsvError(null);
	};

	return (
		<div className="validation-filters">
			<Tabs value={activeTab} onSelected={(val) => setActiveTab(val)} styleProps={{ fontSize: '14px' }}>
				<Tab value="filters" label="Build with filters" />
				<Tab value="voiceIds" label="Paste voice IDs" />
			</Tabs>
			<form id="filters" onSubmit={handleSubmit(onFormSubmit)}>

					{activeTab === 'voiceIds' && (
						<div className="validation-filters__voice-ids">
							<span className="validation-filters__label">VOICE IDS</span>
							<textarea
								className="validation-filters__voice-ids-input"
								value={voiceIdsText}
								onChange={(e) => setVoiceIdsText(e.target.value)}
								placeholder={"Paste voice IDs, comma-separated or one per line:\n17333743290077xxxx\n17422073224403xxxx"}
								rows={6}
							/>
							{voiceIdsError && (
								<span className="validation-filters__voice-ids-error">{voiceIdsError}</span>
							)}
							<span className="validation-filters__hint">{voiceIdsParsed.length} IDs entered</span>
							<label className="validation-filters__checkbox-label">
								<Checkbox
									checked={combineWithFilters}
									onChange={(e) => setCombineWithFilters(e.target.checked)}
								/>
								<span className="validation-filters__label">Also apply the filters</span>
								<Tooltip
									placement="top"
									title={'Validate only the voices that are both in this ID list and matching the filters on the "Build with filters" tab.\nIDs that do not exist or do not match are skipped.\nWith no filters set this behaves exactly like the ID list on its own.'}
								>
									<span
										className="validation-filters__help"
										aria-label="Combined mode help"
										role="button"
										tabIndex={0}
									>
										?
									</span>
								</Tooltip>
							</label>
						</div>
					)}

					{activeTab === 'filters' && combineWithFilters && (
						<div className="validation-filters__combined-notice">
							<span>
								Restricted to the {voiceIdsParsed.length} ID{voiceIdsParsed.length === 1 ? '' : 's'} on
								the &ldquo;Paste voice IDs&rdquo; tab. Only voices in that list matching these filters are validated.
							</span>
							<Button
								type="button"
								size="small"
								variant="tertiary"
								onClick={() => setCombineWithFilters(false)}
							>
								Use filters only
							</Button>
						</div>
					)}

					{activeTab === 'filters' && (
					<>
					<div className="validation-filters__grid">
						<div className="validation-filters__field">
							<span className="validation-filters__label validation-filters__label--with-help">
								Voice Name
								<Tooltip
									placement="top"
									title={'Enter any term to search for voice and profile names, or use \u201Cprofiles.id:xxxxx\u201D to search by profile.id only.\nFor searches in chinese language is better to enter the terms within quotation marks to get more accurate results.\nFor URL searches, wrap the full URL in double quotes for accurate results, e.g. "https://www.instagram.com/james_sleaford".'}
								>
									<span
										className="validation-filters__help"
										aria-label="Search term help"
										role="button"
										tabIndex={0}
									>
										?
									</span>
								</Tooltip>
							</span>
							<Controller
								name="term"
								control={control}
								render={({ field }) => (
									<Input
										startIcon={<SearchOutline />}
										className="input"
										value={field.value}
										onChange={(ev) => field.onChange(ev.target.value)}
										placeholder="Enter term..."
									/>
								)}
							/>
						</div>
						<div className="validation-filters__field validation-filters__field--row">
							<div className="validation-filters__sub-field">
								<span className="validation-filters__label">Type</span>
								<Controller
									name="type"
									control={control}
									render={({ field }) => (
										<GenericDropdownMenu
											key="type"
											field="type"
											menuName="Types"
											options={voiceOptions}
											onChange={(_name, values) => field.onChange(values)}
											selectedOption={field.value.join(',')}
										/>
									)}
								/>
							</div>
							<div className="validation-filters__sub-field">
								<span className="validation-filters__label">Social networks</span>
								<Controller
									name="channel"
									control={control}
									render={({ field }) => (
										<GenericDropdownMenu
											key="channel"
											field="channel"
											menuName="Network"
											options={channelOptions}
											onChange={(_name, values) => field.onChange(values)}
											selectedOption={field.value.join(',')}
										/>
									)}
								/>
							</div>
						</div>

						<div className="validation-filters__field">
							<span className="validation-filters__label">Country</span>
							{loadingCountries ? <Loader size={20} /> : (
								<Controller
									name="country"
									control={control}
									render={({ field }) => (
										<GenericDropdownMenu
											key={`country-${countryOptions.length}`}
											field="country"
											menuName="Countries"
											options={countryOptions}
											onChange={(_name, values) => field.onChange(values)}
											selectedOption={field.value.join(',')}
										/>
									)}
								/>
							)}
						</div>

						<div className="validation-filters__field validation-filters__field--row">
							<div className="validation-filters__sub-field">
								<span className="validation-filters__label">Status</span>
								<Controller
									name="status"
									control={control}
									render={({ field }) => (
										<GenericDropdownMenu
											key="status"
											field="status"
											menuName="Statuses"
											options={STATUS_OPTIONS}
											onChange={(_name, values) => field.onChange(values)}
											selectedOption={field.value.join(',')}
										/>
									)}
								/>
							</div>
							<div className="validation-filters__sub-field">
								<span className="validation-filters__label">Validated</span>
								<Controller
									name="validated"
									control={control}
									render={({ field }) => (
										<GenericDropdownMenu
											key="validated"
											field="validated"
											menuName="Validated"
											options={VALIDATED_OPTIONS}
											onChange={(_name, values) => {
												const latest = values.filter(v => !field.value.includes(v));
												field.onChange(latest.length ? [latest[latest.length - 1]] : []);
											}}
											selectedOption={field.value.join(',')}
										/>
									)}
								/>
							</div>
						</div>

						<div className="validation-filters__field validation-filters__field--row validation-filters__field--span-2">
							<div className="validation-filters__sub-field">
								<span className="validation-filters__label">Last Edit Date (From)</span>
								<Controller
									name="last_edited_date_start"
									control={control}
									render={({ field }) => (
										<DatePicker
											allowRange={false}
											selectedDate={field.value}
											onValueChange={(date) => field.onChange(date)}
											dateFormat="yyyy/MM/dd"
											maxDate={new Date()}
											placeholder="Start date"
										/>
									)}
								/>
							</div>
							<div className="validation-filters__sub-field">
								<span className="validation-filters__label">Last Edit Date (To)</span>
								<Controller
									name="last_edited_date_end"
									control={control}
									render={({ field }) => (
										<DatePicker
											allowRange={false}
											selectedDate={field.value}
											onValueChange={(date) => field.onChange(date)}
											dateFormat="yyyy/MM/dd"
											maxDate={new Date()}
											placeholder="End date"
										/>
									)}
								/>
							</div>
						</div>

                        <div className="validation-filters__field">
                            <span className="validation-filters__label">Labels</span>
                            {loadingLabels || !labelOptions.length ? (
                                <Controller
                                    name="labels"
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            className="input"
                                            value={(field.value ?? []).join(',')}
                                            onChange={(ev) => field.onChange(ev.target.value.split(','))}
                                            onBlur={(ev) => field.onChange(
                                                ev.target.value
                                                    .split(',')
                                                    .map(item => item.trim())
                                                    .filter(Boolean)
                                            )}
                                            placeholder="Labels"
                                        />
                                    )}
                                />
                            ) : (
                                <Controller
                                    name="labels"
                                    control={control}
                                    render={({ field }) => (
                                        <GenericDropdownMenu
                                            key={`labels-${labelOptions.length}`}
                                            field="labels"
                                            menuName="Labels"
                                            options={labelOptions}
                                            onChange={(_name, values) => field.onChange(values)}
                                            selectedOption={field.value.join(',')}
                                        />
                                    )}
                                />
                            )}
                        </div>

						<div className="validation-filters__field">
							<span className="validation-filters__label">Last Edited By</span>
							{loadingEditedByUsers ? <Loader size={20} /> : (
								<Controller
									name="last_edited_by"
									control={control}
									render={({ field }) => (
										<GenericDropdownMenu
											key={`edited-by-${editedByOptions.length}`}
											field="last_edited_by"
											menuName="Edited By"
											options={editedByOptions}
											onChange={(_name, values) => field.onChange(values)}
											selectedOption={field.value.join(',')}
										/>
									)}
								/>
							)}
						</div>

						<div className="validation-filters__field">
							<span className="validation-filters__label validation-filters__label--with-help">
								Uploaded CSV File
								<Tooltip
									placement="top"
									title="A newly uploaded file needs a few minutes to be processed. Until it finishes, filtering by that file returns no voices."
								>
									<span
										className="validation-filters__help"
										aria-label="Uploaded CSV file help"
										role="button"
										tabIndex={0}
									>
										?
									</span>
								</Tooltip>
							</span>
							{loadingCsvFiles ? <Loader size={20} /> : (
								<Controller
									name="upload_csv_file_id"
									control={control}
									render={({ field }) => (
										<GenericDropdownMenu
											field="upload_csv_file_id"
											menuName="CSV Files"
											options={csvFileOptions}
											onChange={(_name, values) => field.onChange(values)}
											selectedOption={field.value.join(',')}
											valueToLabel={csvNamesById}
											onSearchChange={setCsvSearch}
										/>
									)}
								/>
							)}
						</div>

						<div className="validation-filters__field">
							<span className="validation-filters__label">Followers</span>
							<div className={`validation-filters__follower-channels${watch('channel').length ? '' : ' validation-filters__follower-channels--disabled'}`}>
								<Controller
									name="follower_channels"
									control={control}
									render={({ field }) => (
										<GenericDropdownMenu
											key={`follower-channels-${watch('channel').join(',')}`}
											field="follower_channels"
											menuName="Channels"
											options={channelOptions.filter(opt => watch('channel').includes(opt.value))}
											onChange={(_name, values) => field.onChange(values)}
											selectedOption={field.value.join(',')}
											disabled={!watch('channel').length}
										/>
									)}
								/>
							</div>
							<div className={`validation-filters__followers${watch('channel').length ? '' : ' validation-filters__followers--disabled'}`}>
								<Controller
									name="followers_min"
									control={control}
									render={({ field }) => (
										<Input
											className="input"
											value={field.value}
											onChange={(ev) => field.onChange(ev.target.value)}
											placeholder="Min (e.g. 10K)"
											disabled={!watch('channel').length}
										/>
									)}
								/>
								<span className="validation-filters__followers-arrow">→</span>
								<Controller
									name="followers_max"
									control={control}
									render={({ field }) => (
										<Input
											className="input"
											value={field.value}
											onChange={(ev) => field.onChange(ev.target.value)}
											placeholder="Max (e.g. 1M)"
											disabled={!watch('channel').length}
										/>
									)}
								/>
							</div>
							<span className="validation-filters__hint">Accepts plain numbers or shorthand: 500, 10K, 1.5M, 2B</span>
						</div>

						<div className="validation-filters__field">
							<Controller
								name="country_confidence_enabled"
								control={control}
								render={({ field: enabledField }) => (
									<>
										<label className="validation-filters__checkbox-label">
											<Checkbox
												checked={enabledField.value}
												onChange={(e) => enabledField.onChange(e.target.checked)}
											/>
											<span className="validation-filters__label">Conf. Score Country</span>
										</label>
										<div className={`validation-filters__range${enabledField.value ? '' : ' validation-filters__range--disabled'}`}>
											<Controller
												name="country_confidence_min"
													control={control}
													render={({ field: minField }) => (
														<Controller
															name="country_confidence_max"
															control={control}
															render={({ field: maxField }) => (
																<>
																	<input
																		type="range"
																		className="validation-filters__slider validation-filters__slider--min"
																		min={0}
																		max={100}
																		value={minField.value}
																		onChange={(e) => {
																			const val = Number(e.target.value);
																			if (val <= maxField.value) minField.onChange(val);
																		}}
																	/>
																	<input
																		type="range"
																		className="validation-filters__slider validation-filters__slider--max"
																		min={0}
																		max={100}
																		value={maxField.value}
																		onChange={(e) => {
																			const val = Number(e.target.value);
																			if (val >= minField.value) maxField.onChange(val);
																		}}
																	/>
																	<div className="validation-filters__range-labels">
																		<span>min <strong>{minField.value}%</strong></span>
																		<span>max <strong>{maxField.value}%</strong></span>
																	</div>
																</>
															)}
														/>
													)}
												/>
										</div>
									</>
								)}
							/>
						</div>

						<div className="validation-filters__field">
							<Controller
								name="type_confidence_enabled"
								control={control}
								render={({ field: enabledField }) => (
									<>
										<label className="validation-filters__checkbox-label">
											<Checkbox
												checked={enabledField.value}
												onChange={(e) => enabledField.onChange(e.target.checked)}
											/>
											<span className="validation-filters__label">Conf. Score Type</span>
										</label>
										<div className={`validation-filters__range${enabledField.value ? '' : ' validation-filters__range--disabled'}`}>
											<Controller
												name="type_confidence_min"
												control={control}
												render={({ field: minField }) => (
													<Controller
														name="type_confidence_max"
														control={control}
														render={({ field: maxField }) => (
															<>
																<input
																	type="range"
																	className="validation-filters__slider validation-filters__slider--min"
																	min={0}
																	max={100}
																	value={minField.value}
																	disabled={!enabledField.value}
																	onChange={(e) => {
																		const val = Number(e.target.value);
																		if (val <= maxField.value) minField.onChange(val);
																	}}
																/>
																<input
																	type="range"
																	className="validation-filters__slider validation-filters__slider--max"
																	min={0}
																	max={100}
																	value={maxField.value}
																	disabled={!enabledField.value}
																	onChange={(e) => {
																		const val = Number(e.target.value);
																		if (val >= minField.value) maxField.onChange(val);
																	}}
																/>
																<div className="validation-filters__range-labels">
																	<span>min <strong>{minField.value}%</strong></span>
																	<span>max <strong>{maxField.value}%</strong></span>
																</div>
															</>
														)}
													/>
												)}
											/>
										</div>
									</>
								)}
							/>
						</div>
					</div>

					<div className="validation-filters__flags">
						<span className="validation-filters__label">Boolean Flags</span>
						<div className="validation-filters__flags-grid">
							<Controller
								name="missing_mandatory_fields"
								control={control}
								render={({ field }) => (
									<label className="validation-filters__flag">
										<Switch
											checked={field.value}
											onChange={(e) => field.onChange(e.target.checked)}
										/>
										<span>Missing mandatory fields</span>
									</label>
								)}
							/>
							<Controller
								name="to_merge_only"
								control={control}
								render={({ field }) => (
									<label className="validation-filters__flag">
										<Switch
											checked={field.value}
											onChange={(e) => field.onChange(e.target.checked)}
										/>
										<span>To Merge only</span>
									</label>
								)}
							/>
							<Controller
								name="has_recent_publications"
								control={control}
								render={({ field }) => (
									<label className="validation-filters__flag">
										<Switch
											checked={field.value}
											onChange={(e) => field.onChange(e.target.checked)}
										/>
										<span>No data (last 12 months)</span>
									</label>
								)}
							/>
							<Controller
								name="has_recent_brand_mentions"
								control={control}
								render={({ field }) => (
									<label className="validation-filters__flag">
										<Switch
											checked={field.value}
											onChange={(e) => field.onChange(e.target.checked)}
										/>
										<span>No branded data (last 12 months)</span>
									</label>
								)}
							/>
							<Controller
								name="voice_name_duplicates"
								control={control}
								render={({ field }) => (
									<label className="validation-filters__flag">
										<Switch
											checked={field.value}
											onChange={(e) => field.onChange(e.target.checked)}
										/>
										<span>Voice-name duplicate flag</span>
									</label>
								)}
							/>
						</div>
						<p className="validation-filters__flags-hint">
							<strong>Missing mandatory</strong> finds voices missing one or more of: Country, Name, or at least 1 linked source. <strong>Voice-name duplicate</strong> finds voices created with a slightly altered name because a potential duplicate was detected at creation time.
						</p>
					</div>
					</>
					)}

					<div className="validation-filters__actions">
						<Button
							type="submit"
							size="small"
							disabled={!canSubmit}
							rightIcon={isBusy ? <Loader size={20} /> : undefined}
						>
							Start Validation
						</Button>
						<Button
							type="button"
							size="small"
							onClick={() => openSaveDialog()}
							disabled={!canSubmit}
							leftIcon={<DeviceFloppyOutline />}
						>
							Save as Validation List
						</Button>
						<Tooltip
							placement="top"
							title={idsRequired && !voiceIdsValid
								? 'Enter a valid list of voice IDs before exporting a CSV.'
								: hasActiveFilters
									? 'Export every voice matching the current search as a CSV file.'
									: 'Apply at least one filter before exporting a CSV.'}
						>
							<span>
								<Button
									type="button"
									size="small"
									variant="tertiary"
									onClick={handleDownloadCsv}
									disabled={!canDownloadCsv}
									leftIcon={<DownloadOutline />}
								>
									Download CSV
								</Button>
							</span>
						</Tooltip>
						<Button
							type="button"
							size="small"
							variant="tertiary"
							onClick={onClearFilters}
							disabled={isBusy}
						>
							Clear filters
						</Button>
					</div>

			<Modal open={csvModalOpen} onOpenChange={(open) => !open && closeCsvModal()}>
				<ModalContent size="medium">
					<ModalHeading>Download CSV</ModalHeading>
					<ModalDescription>
						<div className="validation-csv-dialog">
							{csvError && (
								<Typography variant="body2" className="validation-csv-dialog__error">{csvError}</Typography>
							)}
							{!csvError && csvCount === null && (
								<div className="validation-csv-dialog__counting">
									<Loader size={20} />
									<Typography variant="body2">Counting matching voices...</Typography>
								</div>
							)}
							{!csvError && csvCount !== null && !csvDownloading && (
								<Typography variant="body2">
									{csvCount === 0 && 'No voices match the current filters.'}
									{csvCount > 0 && csvCount <= CSV_EXPORT_LIMIT && (
										`${formatVoiceCount(csvCount)} voices match the current filters. The export contains one row per voice with its ID, name, country, associated social accounts and online/print medias.`
									)}
									{csvCount > CSV_EXPORT_LIMIT && (
										`${formatVoiceCount(csvCount)} voices match the current filters, which is over the ${CSV_EXPORT_LIMIT.toLocaleString()} export limit. Please narrow down the filters and try again.`
									)}
								</Typography>
							)}
							{!csvError && csvDownloading && (
								<ProgressBar
									value={csvFetched}
									max={Math.min(csvCount, CSV_EXPORT_LIMIT)}
									loading
									hint={`Fetching voices... ${csvFetched.toLocaleString()} / ${Math.min(csvCount, CSV_EXPORT_LIMIT).toLocaleString()}`}
									completedHint="Building the CSV file..."
								/>
							)}
						</div>
					</ModalDescription>
					<ModalActions>
						<ModalButton size="small" variant="tertiary" onClick={() => { closeCsvModal(); return true; }}>
							{csvDownloading ? 'Cancel' : 'Close'}
						</ModalButton>
						{!csvError && csvCount > 0 && csvCount <= CSV_EXPORT_LIMIT && !csvDownloading && (
							<ModalButton size="small" onClick={() => { runCsvExport(); return false; }}>
								Download
							</ModalButton>
						)}
					</ModalActions>
				</ModalContent>
			</Modal>

			<Modal open={saveDialogOpen} onOpenChange={(open) => !open && setSaveDialogOpen(false)}>
				<ModalContent size="medium">
					<ModalHeading>Save as Validation List</ModalHeading>
					<ModalDescription>
						<div className="validation-save-dialog">
							<label className="validation-save-dialog__label">LIST NAME</label>
							<Input
								className="input"
								value={listName}
								onChange={(ev) => setListName(ev.target.value)}
								placeholder="e.g. Inactive EU – April 2026"
							/>
							<Typography variant="body2" className="validation-save-dialog__hint">
								The current filter combination will be saved and can be reused.
							</Typography>
						</div>
					</ModalDescription>
					<ModalActions>
						<ModalButton size="small" variant="tertiary" onClick={() => { setSaveDialogOpen(false); return true; }}>
							Cancel
						</ModalButton>
						<ModalButton
							size="small"
							disabled={!listName.trim() || saving}
							onClick={() => { handleSaveList(false); return false; }}
						>
							{saving ? 'Saving...' : 'Save List'}
						</ModalButton>
						{listId && (
							<ModalButton
								size="small"
								disabled={!listName.trim() || saving}
								onClick={() => { handleSaveList(true); return false; }}
							>
								Save as New List
							</ModalButton>
						)}
					</ModalActions>
				</ModalContent>
			</Modal>
		</form>
		</div>
	)
}

VoiceValidationSearchForm.propTypes = {
	loading: PropTypes.bool,
	searchParams: PropTypes.object.isRequired
};

export default VoiceValidationSearchForm;
